/**
 * @testrelic/playwright-analytics — Main entry point
 *
 * Default export: TestRelicReporter class
 * Named export: recordNavigation helper
 */

export { default } from './reporter.js';

export type {
  NavigationType,
  TestRunReport,
  Summary,
  CIMetadata,
  TimelineEntry,
  TestResult,
  FailureDiagnostic,
  NetworkStats,
  ReporterConfig,
  ApiCallRecord,
  ApiAssertion,
  AssertionType,
  AssertionLocation,
  StepTestIdentity,
  ApiCallStepRequest,
  ApiCallStepResponse,
  StepAssertion,
  NavigationStep,
  ApiCallStep,
  TimelineStep,
  TestRelicDataPayload,
  CloudUploadReceipt,
  WithCloudUpload,
} from './types.js';

export {
  PAYLOAD_VERSION,
  ATTACHMENT_NAME,
  ATTACHMENT_CONTENT_TYPE,
  isTestRelicDataPayload,
} from './types.js';

export { SCHEMA_VERSION } from './schema.js';

export { testRelicFixture } from './fixture.js';
export { testRelicApiFixture } from './api-fixture.js';
export { defineConfig } from './define-config.js';

import type { NavigationType, NavigationAnnotation } from '@testrelic/core';

let warned = false;

/**
 * Records a manual navigation event in the current test's annotations.
 * Use for navigation the auto-tracker cannot detect (iframes, web workers).
 *
 * Requires access to the current test info. If not available, logs a
 * warning once and no-ops.
 */
export function recordNavigation(
  testInfo: { annotations: Array<{ type: string; description?: string }> } | null | undefined,
  url: string,
  navigationType: NavigationType = 'manual_record',
): void {
  if (!testInfo || !testInfo.annotations) {
    if (!warned) {
      warned = true;
      process.stderr.write('[testrelic] recordNavigation: reporter not active, navigation not recorded\n');
    }
    return;
  }

  const annotation: NavigationAnnotation = {
    url,
    navigationType,
    timestamp: new Date().toISOString(),
  };

  testInfo.annotations.push({
    type: 'lambdatest-navigation',
    description: JSON.stringify(annotation),
  });
}
